// SPA-side logout.
//
// **Flow (fn-45.4 — POST logout):**
//   1. User clicks "Sign out"; the app navigates to `/logout` (this
//      component).
//   2. This component issues a same-origin `POST /auth/logout` via
//      `fetch` (credentials included) carrying the CSRF custom header.
//      The BFF token-based-revokes the console refresh family, clears
//      both `__Host-` cookies, and returns `{ logoutUrl }`.
//   3. The component top-level-navigates to that Cognito hosted-UI
//      `/logout` URL so the browser also drops the SSO cookie.
//
// **Why POST + fetch (not a GET navigation):** the BFF logout is POST-only
// (a cross-site GET can no longer force a logout) and CSRF-guarded (a
// custom header a cross-site form can't set). A `fetch` POST returns the
// Cognito logout URL as JSON; we then navigate. A failure still sends the
// user to the fallback (`/login`) so they're never stranded.

import { useEffect } from 'react';

/** The CSRF custom header the BFF preHandler requires on unsafe methods. */
const CSRF_HEADER = 'X-Requested-With';

/** The BFF logout endpoint the SPA POSTs to. */
export function buildLogoutUrl(opts: { readonly bffLogoutPath?: string } = {}): string {
  return opts.bffLogoutPath ?? '/auth/logout';
}

export interface LogoutRedirectProps {
  readonly bffLogoutPath?: string;
  readonly fallbackPath?: string;
  /** Test seam — top-level navigation. */
  readonly navigate?: (url: string) => void;
  /** Test seam — fetch override. */
  readonly fetchFn?: typeof fetch;
}

/**
 * Perform the POST-logout handshake. Exported for tests. Resolves to the
 * URL the caller should navigate to — the Cognito logout URL on success,
 * the fallback path on any failure (the BFF already cleared the cookies if
 * the request reached it).
 */
export async function performLogout(opts: {
  bffLogoutPath: string;
  fallbackPath: string;
  fetchFn: typeof fetch;
}): Promise<string> {
  try {
    const res = await opts.fetchFn(opts.bffLogoutPath, {
      method: 'POST',
      credentials: 'include',
      headers: { [CSRF_HEADER]: 'fetch', Accept: 'application/json' },
    });
    if (res.ok) {
      const body = (await res.json().catch(() => null)) as { logoutUrl?: unknown } | null;
      if (body !== null && typeof body.logoutUrl === 'string' && body.logoutUrl.length > 0) {
        return body.logoutUrl;
      }
    }
  } catch {
    // Network failure — fall through to the local fallback.
  }
  return opts.fallbackPath;
}

export function LogoutRedirect(props: LogoutRedirectProps): React.ReactElement {
  useEffect(() => {
    const bffLogoutPath = buildLogoutUrl(
      props.bffLogoutPath !== undefined ? { bffLogoutPath: props.bffLogoutPath } : {},
    );
    const fallbackPath = props.fallbackPath ?? '/login';
    const fetchFn = props.fetchFn ?? globalThis.fetch.bind(globalThis);
    const nav = props.navigate ?? ((u: string) => window.location.assign(u));
    let cancelled = false;
    void performLogout({ bffLogoutPath, fallbackPath, fetchFn }).then((url) => {
      if (!cancelled) nav(url);
    });
    return () => {
      cancelled = true;
    };
  }, [props.bffLogoutPath, props.fallbackPath, props.navigate, props.fetchFn]);

  return (
    <main className="auth-redirect" aria-live="polite">
      <p>Signing out…</p>
    </main>
  );
}
